import { useCallback } from "react";
import { useCurrencySymbol } from "../../../../hooks/useUtils";
import { getCurrentLanguage } from "../../../../i18n/utils";
import utils from "../../../../utils/utils";

/**
 * 红包雨金额显示：按币种小数位、向下截断（不四舍五入进位）。
 *
 * 小数位规则与后端 Currency.amountScare 一致：BTC / ETH 10 位、PHP 3 位、其余 2 位
 * （红包金额、奖池扣减都按这个精度落库，前端多显示 / 少显示都会和实发对不上）。
 *
 * 为什么不直接用 utils.formatLocaleNumber(n, null, scale)：它内部用 Math.floor(n × 10^scale) 截断，
 * 浮点误差会把 0.29 截成 0.28（0.29 × 100 = 28.999…），这里先按 15 位有效数字规整再截断。
 * app 里也没有按币种取 amountScare 的现成方法（getCurrencyFixed 取的是 startup 的 decimalLength，
 * 且不传 startupData 时恒为 9），故在此按后端规则实现。
 */
export function rainAmountScale(currencyId?: string | null): number {
  switch ((currencyId || "").toUpperCase()) {
    case "BTC":
    case "ETH":
      return 10;
    case "PHP":
      return 3;
    default:
      return 2;
  }
}

/** 向下截断到 scale 位（先规整浮点误差，避免 0.29 → 0.28） */
export function truncateRainAmount(n: number, scale: number): number {
  if (!Number.isFinite(n)) return 0;
  const factor = Math.pow(10, scale);
  const scaled = Number((n * factor).toPrecision(15));
  return Math.trunc(scaled) / factor;
}

/** 与 utils 的本地化口径一致：pt → pt-BR、id → id-ID、bn → bn，其余 en */
function rainLocale(): string {
  const lang = String(getCurrentLanguage() || "en");
  if (lang.startsWith("pt")) return "pt-BR";
  if (lang.startsWith("id")) return "id-ID";
  if (lang.startsWith("bn")) return "bn";
  return "en";
}

/** 当前币种（与 useCurrencySymbol 同源：localStorage currencyId） */
export function getRainCurrencyId(): string {
  const id = utils.getObject("currencyId");
  return typeof id === "string" ? id : "";
}

/**
 * 金额数字部分：截断到币种小数位；至少显示 min(2, scale) 位，多出的末尾 0 不补
 * （BTC 等 10 位币种不会显示一长串 0）
 */
export function formatRainAmountNumber(
  n: number,
  currencyId?: string | null
): string {
  const scale = rainAmountScale(currencyId);
  const v = truncateRainAmount(n, scale);
  try {
    return v.toLocaleString(rainLocale(), {
      minimumFractionDigits: Math.min(2, scale),
      maximumFractionDigits: scale,
    });
  } catch {
    return v.toFixed(Math.min(2, scale));
  }
}

/** 货币符号 + 金额 */
export function formatRainAmount(
  n: number,
  currencySymbol: string,
  currencyId?: string | null
): string {
  return `${currencySymbol}${formatRainAmountNumber(n, currencyId)}`;
}

/** 组件内用：返回「货币符号 + 按币种截断的金额」格式化函数 */
export function useRainAmountFormatter(): (n: number) => string {
  const currencySymbol = useCurrencySymbol();
  return useCallback(
    (n: number) => formatRainAmount(n, currencySymbol, getRainCurrencyId()),
    [currencySymbol]
  );
}
